import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Map, Upload, Trophy, Sparkles, Users, CreditCard } from "lucide-react";

const FeaturesSection = () => {
  const features = [
    {
      icon: Map,
      title: "Trail Mapping & Discovery",
      description: "Explore detailed trail segments enriched with real community data — photos, run reports, and difficulty feedback from fellow adventurers. Find your next adventure with confidence.",
      badge: "Core Feature"
    },
    {
      icon: Upload,
      title: "GPX Track Upload",
      description: "Upload your GPX files from runs and automatically match them to trail segments. Build the community database with every adventure.",
      badge: "Community Driven"
    },
    {
      icon: Trophy,
      title: "Progress Tracking",
      description: "Track your off-road journey with achievements and local leaderboards. Earn recognition for every mile explored and every contribution you make to the community.",
      badge: "Engagement"
    },
    {
      icon: Sparkles,
      title: "AI-Powered Trail Research",
      description: "Our AI automatically researches every trail — pulling together descriptions, conditions, and video guides so you can skip the forum digging and focus on the adventure.",
      badge: "AI Powered"
    },
    {
      icon: Users,
      title: "Clubs & Group Adventures",
      description: "Create or join clubs to share runs privately with trusted members. Coordinate group trips, plan events, and build your off-road community.",
      badge: "Coming Soon"
    },
    {
      icon: CreditCard,
      title: "Unlock More",
      description: "Premium features include route creation, advanced analytics, and event organization tools to take your adventures further.",
      badge: "Premium"
    }
  ];

  return (
    <section id="features" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Platform Features
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Everything You Need for 
            <span className="text-primary"> Off-Road Adventures</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From planning your next adventure to sharing your stories, Airing Down helps you 
            explore, track, and connect with fellow off-road adventurers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        feature.badge === 'AI Powered' ? 'border-accent text-accent' :
                        feature.badge === 'Coming Soon' ? 'border-trail-moderate text-trail-moderate' :
                        feature.badge === 'Premium' ? 'border-primary text-primary' :
                        'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {feature.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
